import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErrorDeApi } from '@nucleo/api/cliente'
import { useTrabajoSinGuardar } from '@nucleo/trabajoSinGuardar'
import { AreaDeTexto } from '@ui/primitivas/AreaDeTexto'
import { Boton } from '@ui/primitivas/Boton'
import { Campo } from '@ui/primitivas/Campo'
import { Selector } from '@ui/primitivas/Selector'
import { agregarTarea, decidirTarea, editarTarea, obtenerMatriz, proponerLevantamiento, validarTareasEnLote } from './clienteDeMatrices'
import { ConfirmacionDeDescarte, Subpaso } from './ComponentesDeEtapa'
import { describirProcedencia, ETIQUETA_DE_ESTADO } from './etiquetas'
import { PerfilesDeCargo } from './RecursosDeMatriz'
import type { Matriz, Procedencia, RecursoDeMatriz, Tarea, TareaEntrada } from './tipos'

const EJEMPLO = `Proceso: Preparación y despacho de pedidos
Área: Embalaje
Puesto: Operador de embalaje
Recibir productos desde preparación y verificar cantidad y estado.
Armar cajas, proteger productos y cerrar paquetes con selladora manual.
Etiquetar los pedidos y trasladar cajas al área de despacho con carro.
Puesto: Supervisor de embalaje
Revisar pedidos observados y coordinar correcciones.
Realizar inventario ocasional de insumos de embalaje.`

/** Marcador breve: no debe parecer texto ya escrito (el ejemplo completo se carga con un botón). */
const MARCADOR = 'Proceso: …\nÁrea: …\nPuesto: …\nUna actividad por línea…'

const OPCIONES_DE_TIPO = [
  { valor: 'rutinaria', etiqueta: 'Rutinaria' },
  { valor: 'no_rutinaria', etiqueta: 'No rutinaria' },
]

export function PasoLevantamiento(props: { matriz: Matriz; alActualizar: (m: Matriz) => void }) {
  const navegar = useNavigate()
  const { matriz } = props
  const [descripcion, setDescripcion] = useState('')
  const [proponiendo, setProponiendo] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<{ agregadas: number; procedencia: Procedencia; descartes: string[] } | null>(null)
  const [editando, setEditando] = useState<Tarea | null>(null)
  const [descartando, setDescartando] = useState<Tarea | null>(null)
  const [agregando, setAgregando] = useState(false)
  const [validandoLote, setValidandoLote] = useState(false)

  const visibles = matriz.tareas.filter((t) => t.estado !== 'descartada')
  const descartadas = matriz.tareas.filter((t) => t.estado === 'descartada')
  const pendientes = visibles.filter((t) => t.estado === 'propuesta')
  const puedeContinuar = matriz.progreso.tareas_validadas > 0
  const largoExcedido = descripcion.length > 12000

  useTrabajoSinGuardar(descripcion.trim().length > 0 && !proponiendo, 'La descripción del proceso')
  useTrabajoSinGuardar(editando !== null || agregando, 'La tarea que estás editando')

  const refrescar = async () => props.alActualizar(await obtenerMatriz(matriz.id))

  const proponer = async () => {
    setProponiendo(true)
    setError(null)
    setAviso(null)
    try {
      const r = await proponerLevantamiento(matriz.id, descripcion)
      props.alActualizar(r.matriz)
      setAviso({ agregadas: r.agregadas, procedencia: r.procedencia, descartes: r.descartes })
      setDescripcion('')
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No fue posible obtener la propuesta. Tu descripción sigue en el cuadro: vuelve a intentarlo.')
    } finally {
      setProponiendo(false)
    }
  }

  const incorporarPerfil = (perfil: RecursoDeMatriz) => {
    const bloque = `Área: ${perfil.area}\nPuesto: ${perfil.cargo}\n${perfil.texto ?? ''}`
    const proximo = [descripcion.trim(), bloque].filter(Boolean).join('\n')
    if (proximo.length > 12000) {
      setError('El borrador supera 12.000 caracteres. Copia solo las actividades pertinentes del perfil.')
      return
    }
    setDescripcion(proximo)
    setError(null)
    document.getElementById('descripcion-del-proceso')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const decidir = async (t: Tarea, decision: 'validar' | 'descartar', motivo?: string) => {
    setError(null)
    try {
      await decidirTarea(matriz.id, t.id, decision, motivo)
      await refrescar()
      setDescartando(null)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No fue posible registrar la decisión.')
    }
  }

  return (
    <div className="grid gap-4">
      <div>
        <p className="cs-eyebrow">Etapa 2 · Levantamiento de procesos</p>
        <h2 className="text-h2">Cuéntale a Atalaya cómo trabaja el área</h2>
      </div>

      <Subpaso
        numero="2.1"
        titulo={visibles.length ? 'Describe más actividades (opcional)' : 'Describe el proceso'}
        detalle="Proceso, área, cada puesto y una actividad por línea. Más detalle concreto produce tareas más ajustadas."
        activo={visibles.length === 0}
      >
        <details className="rounded-lg border border-borde px-4" open={visibles.length === 0}>
          <summary className="text-pequeno font-semibold">Qué escribir y qué evitar</summary>
          <div className="grid gap-2 pb-3 text-pequeno text-texto-secundario">
            <p>Indica el proceso, el área, cada cargo y una acción por línea. En Embalaje, por ejemplo, explica desde la recepción del producto hasta el despacho.</p>
            <ul className="list-disc pl-5">
              <li>Qué se hace y en qué orden, incluyendo tareas ocasionales.</li>
              <li>Qué equipos, herramientas, materiales y traslados intervienen.</li>
              <li>Qué condiciones del sector cambian el trabajo: espacio, turnos o interrupciones.</li>
            </ul>
            <p>Describe cargos y actividades, sin nombres, RUT ni datos de trabajadores. Evita frases vagas como «hacer embalaje».</p>
          </div>
        </details>

        <PerfilesDeCargo matrizId={matriz.id} alUsar={incorporarPerfil} />

        {proponiendo ? (
          <div className="atalaya-propuesta-cargando" role="status" aria-live="polite">
            <div className="atalaya-emblema" aria-hidden="true"><img src="/chilesst/chilesst-mark.svg" alt="" /></div>
            <p className="text-h3">Atalaya está leyendo el proceso y proponiendo tareas…</p>
            <p className="text-pequeno text-texto-secundario">Nada queda registrado como oficial: después revisas cada tarea.</p>
          </div>
        ) : (
          <div id="descripcion-del-proceso" className="grid gap-3">
            <AreaDeTexto
              etiqueta="Descripción del proceso"
              valor={descripcion}
              alCambiar={setDescripcion}
              filas={8}
              marcador={MARCADOR}
              ayuda={`«Proceso:», «Área:» y «Puesto:» sitúan las líneas siguientes. ${descripcion.length.toLocaleString('es-CL')} de 12.000 caracteres.`}
              {...(largoExcedido ? { error: 'Supera los 12.000 caracteres: deja solo las actividades pertinentes.' } : {})}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Boton variante="sutil" compacto alPulsar={() => setDescripcion(EJEMPLO)}>Cargar un ejemplo de embalaje</Boton>
              <Boton variante={visibles.length ? 'secundario' : 'primario'} alPulsar={() => void proponer()} deshabilitado={!descripcion.trim() || largoExcedido}>
                {visibles.length ? 'Proponer más tareas' : 'Proponer tareas'}
              </Boton>
            </div>
          </div>
        )}
      </Subpaso>

      {error ? <p role="alert" className="rounded-md bg-critico-fondo p-3 text-critico-texto">{error}</p> : null}
      {aviso ? (
        <p role="status" className="rounded-md bg-informativo-fondo p-3 text-pequeno text-informativo-texto">
          {aviso.agregadas ? `Atalaya propuso ${aviso.agregadas} tarea(s). ` : 'No hubo tareas nuevas (las que ya existían no se duplican). '}
          {describirProcedencia(aviso.procedencia.origen === 'ia' ? 'ia' : 'reglas', aviso.procedencia)}
          {aviso.descartes.length ? ` Se descartaron ${aviso.descartes.length} elemento(s) inválido(s).` : ''}
        </p>
      ) : null}

      {visibles.length ? (
        <Subpaso
          numero="2.2"
          titulo="Revisa las tareas"
          detalle={pendientes.length ? `${pendientes.length} de ${visibles.length} pendiente(s) de tu revisión. Da visto bueno, edita o descarta cada una.` : `Todas revisadas: ${matriz.progreso.tareas_validadas} con visto bueno.`}
          activo={pendientes.length > 0}
          acciones={pendientes.length ? (
            <Boton
              variante="secundario"
              compacto
              trabajando={validandoLote}
              alPulsar={() => {
                setValidandoLote(true)
                validarTareasEnLote(matriz.id)
                  .then(refrescar)
                  .catch(() => setError('No fue posible validar en lote.'))
                  .finally(() => setValidandoLote(false))
              }}
            >
              Visto bueno a las {pendientes.length} pendientes
            </Boton>
          ) : null}
        >
          <ol className="grid gap-2" aria-label="Tareas">
            {visibles.map((t, i) => (
              <li key={t.id} className={`grid gap-2 rounded-lg border p-3 ${t.estado === 'propuesta' ? 'border-atencion/60 bg-atencion-fondo/40' : 'border-borde'}`}>
                {editando?.id === t.id ? (
                  <FormularioDeTarea
                    inicial={t}
                    alCancelar={() => setEditando(null)}
                    alGuardar={(e) => {
                      editarTarea(matriz.id, t.id, e)
                        .then(refrescar)
                        .then(() => setEditando(null))
                        .catch((err: unknown) => setError(err instanceof ErrorDeApi ? err.message : 'No fue posible editar.'))
                    }}
                  />
                ) : descartando?.id === t.id ? (
                  <ConfirmacionDeDescarte que={`la tarea T${i + 1}`} alCancelar={() => setDescartando(null)} alConfirmar={(motivo) => void decidir(t, 'descartar', motivo)} />
                ) : (
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="grid min-w-0 gap-1">
                      <span className="text-pequeno text-texto-secundario">
                        <strong className="text-texto">T{i + 1}</strong> · {t.proceso} · {t.area} · <strong className="text-texto">{t.puesto}</strong> · {t.tipo === 'rutinaria' ? 'Rutinaria' : 'No rutinaria'}
                      </span>
                      <span className="text-cuerpo font-semibold">{t.tarea}</span>
                      <span className="text-pequeno text-texto-secundario">
                        <span className={`cs-state ${t.estado === 'propuesta' ? 'cs-state--soon' : 'cs-state--live'}`}>{ETIQUETA_DE_ESTADO[t.estado]}</span> {describirProcedencia(t.origen, undefined)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {t.estado === 'propuesta' ? <Boton variante="afirmativo" compacto alPulsar={() => void decidir(t, 'validar')} etiquetaAccesible={`Visto bueno a la tarea ${i + 1}`}>✓ Visto bueno</Boton> : null}
                      <Boton variante="secundario" compacto alPulsar={() => { setEditando(t); setDescartando(null) }} etiquetaAccesible={`Editar la tarea ${i + 1}`}>Editar</Boton>
                      <Boton variante="peligro" compacto alPulsar={() => { setDescartando(t); setEditando(null) }} etiquetaAccesible={`Descartar la tarea ${i + 1}`}>Descartar</Boton>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ol>
          <div className="rounded-lg border border-dashed border-borde p-3">
            {agregando ? (
              <FormularioDeTarea
                inicial={{ proceso: visibles[0]?.proceso ?? matriz.alcance, area: visibles[0]?.area ?? '', puesto: visibles[0]?.puesto ?? '', tarea: '', tipo: 'rutinaria' }}
                alCancelar={() => setAgregando(false)}
                alGuardar={(e) => {
                  agregarTarea(matriz.id, e)
                    .then(refrescar)
                    .then(() => setAgregando(false))
                    .catch((err: unknown) => setError(err instanceof ErrorDeApi ? err.message : 'No fue posible agregar.'))
                }}
              />
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-pequeno text-texto-secundario">¿Falta una tarea que Atalaya no propuso? Agrégala tú: queda con visto bueno de inmediato.</p>
                <Boton variante="secundario" compacto alPulsar={() => setAgregando(true)}>Agregar tarea a mano</Boton>
              </div>
            )}
          </div>
        </Subpaso>
      ) : null}

      {descartadas.length ? (
        <details className="sst-tarjeta px-4">
          <summary className="text-pequeno font-semibold">Tareas descartadas ({descartadas.length})</summary>
          <ul className="grid gap-1 pb-3 text-pequeno text-texto-secundario">
            {descartadas.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>{t.tarea}{t.motivo_descarte ? ` — ${t.motivo_descarte}` : ''}</span>
                <Boton variante="sutil" compacto alPulsar={() => void decidir(t, 'validar')}>Recuperar</Boton>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <div className="sst-barra-cierre" role="region" aria-label="Cerrar la etapa 2">
        <p id="estado-cierre-levantamiento">
          {!puedeContinuar
            ? 'Para continuar, da visto bueno al menos a una tarea: Atalaya propone sus peligros, evaluación y medidas en la etapa 3.'
            : pendientes.length
              ? `Puedes continuar con ${matriz.progreso.tareas_validadas} tarea(s) con visto bueno; las ${pendientes.length} pendientes quedan fuera hasta que las revises.`
              : `Todo listo: ${matriz.progreso.tareas_validadas} tarea(s) con visto bueno.`}
        </p>
        <Boton deshabilitado={!puedeContinuar} describidoPor="estado-cierre-levantamiento" alPulsar={() => navegar(`/matrices/${matriz.id}/matriz`)}>
          Ir a la matriz asistida
        </Boton>
      </div>
    </div>
  )
}

export function FormularioDeTarea(props: { inicial: TareaEntrada; alGuardar: (t: TareaEntrada) => void; alCancelar: () => void }) {
  // Solo los campos del contrato `TareaEntrada`: el servidor rechaza campos extra (422) y
  // `inicial` puede ser una `Tarea` completa (id, estado, origen…).
  const [t, setT] = useState<TareaEntrada>(() => ({
    proceso: props.inicial.proceso, area: props.inicial.area, puesto: props.inicial.puesto, tarea: props.inicial.tarea, tipo: props.inicial.tipo,
  }))
  const completa = t.proceso.trim() && t.area.trim() && t.puesto.trim() && t.tarea.trim()
  return (
    <form
      className="grid gap-2"
      aria-label="Datos de la tarea"
      onSubmit={(e) => {
        e.preventDefault()
        if (completa) props.alGuardar(t)
      }}
    >
      <div className="grid gap-2 md:grid-cols-4">
        <Campo etiqueta="Proceso" valor={t.proceso} alCambiar={(v) => setT({ ...t, proceso: v })} requerido />
        <Campo etiqueta="Área de trabajo" valor={t.area} alCambiar={(v) => setT({ ...t, area: v })} requerido />
        <Campo etiqueta="Puesto de trabajo" valor={t.puesto} alCambiar={(v) => setT({ ...t, puesto: v })} requerido />
        <Selector etiqueta="Tipo" valor={t.tipo} opciones={OPCIONES_DE_TIPO} alCambiar={(v) => setT({ ...t, tipo: v === 'no_rutinaria' ? 'no_rutinaria' : 'rutinaria' })} />
      </div>
      <Campo etiqueta="Tarea" valor={t.tarea} alCambiar={(v) => setT({ ...t, tarea: v })} requerido autoFoco />
      <div className="flex flex-wrap justify-end gap-2">
        <Boton variante="secundario" compacto alPulsar={props.alCancelar}>Cancelar</Boton>
        <Boton variante="afirmativo" tipo="submit" compacto deshabilitado={!completa}>Guardar tarea</Boton>
      </div>
    </form>
  )
}
